import { Box, Tooltip, Typography, GlobalStyles } from '@mui/material';

import { CognitoContext } from '@/features/authentication/components/CognitoProvider';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { FOVOverlay } from '@/features/ses/components/FOVOverlay';

import useCameraPtzKeyboardBindings from '../../camera/hooks/useCameraPtzKeyboardBindings';
import useCameraSelectionBindings from '../../camera/hooks/useCameraSelectionBindings';

import { usePTZStore } from '@/features/camera/store/usePTZStore';
import { ViewType } from '@/features/camera/types/VideoSource';
import JumpFeedbackOverlay from '@/features/video/components/JumpFeedbackOverlay';
import PlayerControls from '@/features/video/components/PlayerControls';
import RotatingIcon from '@/shared/components/RotatingIcon';
import { usePTZSocket } from '@/shared/hooks/websocket/usePTZSocket';
import { CircleX as CloseIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useFullscreen from '../../video/hooks/useFullscreen';
import usePlayer, { UsePlayerParams } from '../../video/hooks/usePlayer';
import { ACCEPTABLE_GRID_SIZES, DEFAULT_ZOOM, VIDEO_PLAYER_ID, ZOOM_FACTOR } from '../constants';
import { usePersistedValue } from '../hooks/usePersistedValue';
import { useWebrtcMetricsCollector } from '../hooks/useWebrtcMetricsCollector';
import { useWebrtcOverlay } from '../hooks/useWebrtcOverlay';
import { GameModel } from '../types';
import { useCurrentGame } from '../context/CurrentGameContext';
import AutoIsoPanelContent from './AutoIsoPanelContent';
import WebrtcMetrics from './WebrtcMetrics';
import useGameTypeMetadataHeight from '@/shared/hooks/useGameTypeMetadataHeight';
import { Group, Panel, useDefaultLayout } from 'react-resizable-panels';
import GripSeparator from '@/shared/components/GripSeparator';
import DraggableGamePosition from './DraggableGamePosition';
import GameSplash from './GameSplash';
import ScoreboardData from './ScoreboardData';
import WheelZoom from './WheelZoom';
import TargetVideoOverlay from '@/features/framingPresets/components/TargetVideoOverlay';

// Constants moved outside component to prevent re-creation
type Tuple = [zoom: number, step: number];

const ZOOM_TUPLES: Tuple[] = [
  [1, 7500],
  [10, 750],
  [80, 500],
];

// Vertical space inside the autoIso panel that's NOT the rink (button, margins, padding).
const AUTOISO_PANEL_OVERHEAD = 90;
// Fallback min height while gameTypeMetadataHeight is still loading (data === undefined → 0).
const AUTOISO_PANEL_FALLBACK_MIN = 230;

// Pure function moved outside component
function interpolateStep(zoom: number): number {
  const n = ZOOM_TUPLES.length;

  if (zoom <= ZOOM_TUPLES[0][0]) return ZOOM_TUPLES[0][1];
  if (zoom >= ZOOM_TUPLES[n - 1][0]) return ZOOM_TUPLES[n - 1][1];

  for (let i = 0; i < n - 1; i++) {
    const [z1, s1] = ZOOM_TUPLES[i];
    const [z2, s2] = ZOOM_TUPLES[i + 1];

    if (zoom >= z1 && zoom <= z2) {
      const t = (zoom - z1) / (z2 - z1);
      return Math.round(s1 + t * (s2 - s1));
    }
  }

  throw new Error('Unexpected error in interpolation.');
}

// Static style objects moved outside component
const CAMERA_LABEL_STYLE = {
  backgroundColor: 'rgba(0,0,0,0.4)',
  padding: '5px',
  borderRadius: '4px',
  position: 'absolute' as const,
  bottom: 100,
  right: 0,
};

const CAMERA_QUADRANT_STYLE = {
  color: 'white',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexDirection: 'column' as const,
  fontWeight: 'bold',
  fontSize: 26,
  cursor: 'pointer',
  position: 'relative' as const,
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
};

const CAMERA_ANGLE_STYLE = {
  padding: '1px',
  borderRadius: '4px',
  position: 'absolute' as const,
  bottom: 0,
  left: 0,
  opacity: 0.2,
  '&:hover': {
    opacity: 1,
  },
  transition: 'opacity 0.2s ease-in-out',
};

const GamePageContents = ({ game, sidebarWidth }: { game: GameModel; sidebarWidth?: number }) => {
  // Debug: Track what triggers re-renders using refs to compare previous values
  const prevPropsRef = useRef({ game, sidebarWidth });
  useEffect(() => {
    const prevProps = prevPropsRef.current;
    const changes: string[] = [];

    if (prevProps.game !== game) changes.push('game');
    if (prevProps.sidebarWidth !== sidebarWidth) changes.push('sidebarWidth');

    prevPropsRef.current = { game, sidebarWidth };
  });
  const [previewIsVisible, setPreviewIsVisible] = usePersistedValue<boolean>(
    'previewIsVisible',
    true,
  );

  const { ref: fullscreenRef, requestFullscreen, isFullscreen, exitFullscreen } = useFullscreen();

  // WebRTC diagnostics overlay: off by default, opt-in only via the console helpers
  // (window.enableWebrtcOverlay()) or the ?webrtcOverlay=1 URL param. See useWebrtcOverlay.
  const webrtcOverlayVisible = useWebrtcOverlay();

  const [playerState, setPlayerState] = useState<string>('idle');
  const videoControls = useRef<HTMLDivElement>(null);

  const { cognitoContext } = useContext(CognitoContext);

  const serverState = useServerStateContext();

  // Debug: Track serverState changes
  const prevServerStateRef = useRef(serverState);
  useEffect(() => {
    if (prevServerStateRef.current !== serverState) {
      prevServerStateRef.current = serverState;
    }
  });

  const [statsReport, setStatsReport] = useState<RTCStatsReport | null>(null);
  const [jumpFeedback, setJumpFeedback] = useState<number | null>(null);

  // WebRTC metrics: the headless collector always runs (ref-based, no re-render), while
  // the live overlay only re-renders when the user has the stats panel open.
  const { collect: collectWebrtcMetrics } = useWebrtcMetricsCollector();
  const statsVisibleRef = useRef(webrtcOverlayVisible);
  statsVisibleRef.current = webrtcOverlayVisible;

  // Min height for the autoIso panel: must fit the rink visual + AutoIsoSelect button +
  // padding. AUTOISO_PANEL_OVERHEAD covers the toggle button (~40px), `mb:3` margin
  // (24px), inner Box `p:1` (16px), outer Panel paddingBottom (8px).
  const gameTypeMetadataHeight = useGameTypeMetadataHeight();
  const autoIsoPanelMinSize = useMemo(
    () => Math.max(AUTOISO_PANEL_FALLBACK_MIN, gameTypeMetadataHeight + AUTOISO_PANEL_OVERHEAD),
    [gameTypeMetadataHeight],
  );
  const {
    selectedCamera = null,
    selectCamera = () => {},
    viewType = 'single',
    availableCameras = [],
  } = serverState || {};

  const { getValue, store } = usePTZStore();
  const { setValue: setPtzVelocity } = usePTZSocket();

  // The hostname is reported by the backend on `session-handler` — it's the very
  // host the cloud player's ingress was provisioned with, so we never rebuild it
  // here. Empty until that event arrives, in which case we hand the player no
  // sources rather than dialing a bogus URL. The stream is the session's, not
  // the game's: until this game's switch is done it still plays the previous
  // game, so the player gets no sources (and the slate stays up) until then.
  const sessionHost = cognitoContext?.sessionHost;
  const { gameSwitched } = useCurrentGame();
  const streamSources = useMemo<UsePlayerParams['sources']>(
    () =>
      sessionHost && gameSwitched
        ? [
            { type: 'webrtc', file: `wss://${sessionHost}/app/stream` },
          ]
        : [],
    [sessionHost, gameSwitched],
  );

  // Debug: Track cognitoContext object identity changes
  const prevCognitoRef = useRef(cognitoContext);
  useEffect(() => {
    if (prevCognitoRef.current !== cognitoContext) {
      prevCognitoRef.current = cognitoContext;
    }
  });

  const { player, stalled } = usePlayer({
    elId: VIDEO_PLAYER_ID,
    sources: streamSources,
    onWebrtcStatsChange: (statsReport) => {
      // Always collect for analytics (cheap, no re-render)...
      collectWebrtcMetrics(statsReport);
      // ...but only drive the live chart when the overlay is actually visible.
      if (statsVisibleRef.current) setStatsReport(statsReport);
    },
    handlers: {
      //@ts-ignore
      stateChanged: ({ prevstate, newstate }) => {
        setPlayerState(newstate);
      },
    },
  });

  const gridSize = useMemo(
    () => ACCEPTABLE_GRID_SIZES.find((size) => size >= availableCameras.length) || 1,
    [availableCameras],
  );

  const gridEdgeSize = useMemo(() => Math.max(gridSize / 2, 1), [gridSize]);

  // PTZ progressive acceleration state
  const ptzInputRef = useRef({ pan: 0, tilt: 0, zoom: 0 });
  const ptzVelocityRef = useRef({ pan: 0, tilt: 0, zoom: 0 });
  const ptzHoldTimeRef = useRef({ pan: 0, tilt: 0, zoom: 0 }); // Track how long each direction is held
  const ptzIntervalRef = useRef<number | null>(null);
  const lastZoomRef = useRef(DEFAULT_ZOOM);

  const handlePtzChange = useCallback(
    (ptzDiff: { panDiff?: number; tiltDiff?: number; zoomDiff?: number }) => {
      if (viewType === ViewType.MOSAIC) {
        return;
      }

      if (typeof selectedCamera?.camIndex === 'undefined') return;

      const { zoom } = getValue?.(selectedCamera.camIndex) || { zoom: DEFAULT_ZOOM };
      lastZoomRef.current = zoom;

      // Update input targets
      ptzInputRef.current = {
        pan: ptzDiff.panDiff ?? 0,
        tilt: ptzDiff.tiltDiff ?? 0,
        zoom: ptzDiff.zoomDiff ?? 0,
      };

      // Start smooth update loop if not running
      if (ptzIntervalRef.current === null) {
        const smoothUpdate = () => {
          const input = ptzInputRef.current;
          const velocity = ptzVelocityRef.current;
          const holdTime = ptzHoldTimeRef.current;

          // Configuration
          const BASE_SPEED = 0.3; // Initial speed multiplier
          const MAX_SPEED = 7.0; // Maximum speed multiplier
          const ACCELERATION_RATE = 0.08; // How quickly it accelerates (frames to add to hold time)
          const DECELERATION_FACTOR = 0.15; // How quickly it decelerates when released (0-1)
          const LOG_SCALE = 2.5; // Controls the logarithmic curve steepness

          // Calculate logarithmic acceleration for each axis
          const calculateSpeed = (direction: number, currentHoldTime: number) => {
            if (direction === 0) return 0;

            // Logarithmic growth: speed = BASE_SPEED + (MAX_SPEED - BASE_SPEED) * log(1 + holdTime) / log(1 + maxHoldTime)
            // This gives a curve that starts fast and gradually slows the rate of acceleration
            const normalizedHoldTime = Math.min(currentHoldTime, 100); // Cap at 100 frames (~1.6s)
            const speedMultiplier =
              BASE_SPEED +
              (MAX_SPEED - BASE_SPEED) *
                (Math.log(1 + normalizedHoldTime * LOG_SCALE) / Math.log(1 + 100 * LOG_SCALE));

            return direction * speedMultiplier;
          };

          // Update hold times and calculate new velocities
          const newPanHoldTime = input.pan !== 0 ? holdTime.pan + ACCELERATION_RATE : 0;
          const newTiltHoldTime = input.tilt !== 0 ? holdTime.tilt + ACCELERATION_RATE : 0;
          const newZoomHoldTime = input.zoom !== 0 ? holdTime.zoom + ACCELERATION_RATE : 0;

          ptzHoldTimeRef.current = {
            pan: newPanHoldTime,
            tilt: newTiltHoldTime,
            zoom: newZoomHoldTime,
          };

          // Calculate target velocities with logarithmic acceleration
          const targetPan = calculateSpeed(input.pan, newPanHoldTime);
          const targetTilt = calculateSpeed(input.tilt, newTiltHoldTime);
          const targetZoom = calculateSpeed(input.zoom, newZoomHoldTime);

          // Smooth transition to target velocity (for deceleration)
          const newPan = input.pan !== 0 ? targetPan : velocity.pan * (1 - DECELERATION_FACTOR);
          const newTilt = input.tilt !== 0 ? targetTilt : velocity.tilt * (1 - DECELERATION_FACTOR);
          const newZoom = input.zoom !== 0 ? targetZoom : velocity.zoom * (1 - DECELERATION_FACTOR);

          // Update velocity state
          ptzVelocityRef.current = {
            pan: newPan,
            tilt: newTilt,
            zoom: newZoom,
          };

          // Calculate scaled velocities for PTZ socket
          const scaledVelocity = {
            pan: newPan / interpolateStep(lastZoomRef.current),
            tilt: newTilt / interpolateStep(lastZoomRef.current),
            zoom: newZoom / ZOOM_FACTOR || 0,
          };

          // Send to PTZ socket
          setPtzVelocity(scaledVelocity);

          // Check if we should stop the loop
          const STOP_THRESHOLD = 0.01;
          const isNearZero =
            Math.abs(velocity.pan) < STOP_THRESHOLD &&
            Math.abs(velocity.tilt) < STOP_THRESHOLD &&
            Math.abs(velocity.zoom) < STOP_THRESHOLD;
          const noInput = input.pan === 0 && input.tilt === 0 && input.zoom === 0;

          if (isNearZero && noInput) {
            if (ptzIntervalRef.current !== null) {
              clearInterval(ptzIntervalRef.current);
              ptzIntervalRef.current = null;
            }
            ptzVelocityRef.current = { pan: 0, tilt: 0, zoom: 0 };
            ptzHoldTimeRef.current = { pan: 0, tilt: 0, zoom: 0 };
            setPtzVelocity({ pan: 0, tilt: 0, zoom: 0 });
          }
        };

        // Run at ~60fps
        ptzIntervalRef.current = window.setInterval(smoothUpdate, 16);
      }
    },
    [viewType, selectedCamera, getValue, setPtzVelocity],
  );

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (ptzIntervalRef.current !== null) {
        clearInterval(ptzIntervalRef.current);
        ptzIntervalRef.current = null;
      }
    };
  }, []);

  useCameraPtzKeyboardBindings(handlePtzChange);

  // Camera selection keyboard shortcuts (1-9)
  const handleCameraSelection = useCallback(
    (cameraIdx: number) => {
      if (!Array.isArray(availableCameras)) return;
      const camera = availableCameras.find((c) => c.camIndex === cameraIdx);
      if (camera) {
        selectCamera(camera.camIndex, 'Keyboard Shortcut');
      }
    },
    [availableCameras, selectCamera],
  );

  useCameraSelectionBindings(handleCameraSelection);

  const playbackAvailable =
    playerState === 'playing' || playerState === 'paused' || playerState === 'loading';

  useEffect(() => {
    if (!Array.isArray(availableCameras) || !availableCameras.length || !store) return;
    availableCameras.forEach((c) => {
      if (store[c.id] !== undefined) {
        c.pan = store[c.id].pan;
      }
    });
  }, [store]);

  const absolute: 'absolute' | 'relative' | 'fixed' = 'absolute';

  const gradientBackground = {
    position: absolute,
    width: '100%',
    left: 0,
    bottom: 0,
    height: '100%',
    backgroundImage:
      'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAC+CAYAAAD0iWUpAAAAAXNSR0IArs4c6QAAAa\
        JJREFUOE890PtrT3Ecx/H3mxnGMJvNLsZsZhvmtjsbYzP3lLSSpCQlSUlKkpKU1lJaStJKUvJH6vH5fp3zw7PX5f0+n885E\
        eXJCnTWgtwgLdhYR2aDvoHdFJHZaLTR+GZZwZYaMmKrYYgmWZPhbVTB9grNVLP37TC8ky3Y9R/ZEpnRwu6uI7NVVtBmrU2x\
        hy1od1q7rEPbIdsr62Q7FV1Ul3O7IyEyemQ92n01ZEavrJfa779AHKAg+nxlH3vQC/pl/YYH2AG7h2QFg+yg4rANiCHDQ9S\
        wbNhdRqgRc0dsHK0hIo5VGKVGFcepghPWIE5WOCUrOC0rGLM2Ro0rJtxggp2kJqkpasoNpl1oWjZjeIY9o4A86/MhZo3MKub\
        YOeqcjfMKyHlqXnuhwkUZ5IKNBXaRXfTmSxTEkgstUZdlV+rIuCqDvOZCENfriLhB3TRyy8ht9g67DHedcU9xn31APaQewWPD\
        T6inrvuMeg4vrL2kXtl4zb5h38I7eG/jA/WR+mRuhVqFz4ov1Br11UHfqO/wA9Z9zE8/+5e53/BH8fcfIHIrEq6e9rEAAAAASUVORK5CYII=")',
    display: 'flex',
    backgroundRepeat: 'repeat-x',
    backgroundPosition: 'bottom',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    transition: `opacity 0.1s cubic-bezier(.4,0,1,1)`,
  };

  // Track the actual container size from the resizable panel
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Use ResizeObserver to track container size changes when panels resize
  useEffect(() => {
    const container = playerContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Calculate player dimensions that maintain 16:9 aspect ratio based on actual container
  const playerDimensions = useMemo(() => {
    const { width: containerWidth, height: containerHeight } = containerSize;

    if (containerWidth === 0 || containerHeight === 0) {
      // Fallback while container is being measured
      return { width: '100%', height: 'auto' };
    }

    const aspectRatio = 16 / 9;

    // Prioritize using full width, then check if height fits
    const heightFromWidth = containerWidth / aspectRatio;

    if (heightFromWidth <= containerHeight) {
      // Width is usable - use full width
      return {
        width: `${containerWidth}px`,
        height: `${heightFromWidth}px`,
      };
    }

    // Height is the constraint - use full height, calculate width
    const widthFromHeight = containerHeight * aspectRatio;
    return {
      width: `${widthFromHeight}px`,
      height: `${containerHeight}px`,
    };
  }, [containerSize]);

  // Memoize player dimensions sx object
  const playerBoxSx = useMemo(
    () => ({
      width: isFullscreen ? '100vw' : playerDimensions.width,
      height: isFullscreen ? '100vh' : playerDimensions.height,
      position: 'relative' as const,
      borderRadius: isFullscreen ? 0 : 1,
      overflow: 'hidden' as const,
      flexShrink: 0,
    }),
    [isFullscreen, playerDimensions],
  );

  // Memoize mosaic grid sx
  const mosaicGridSx = useMemo(
    () => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      aspectRatio: '16/9',
      display: 'grid',
      gridTemplateColumns: `repeat(${gridEdgeSize}, 1fr)`,
    }),
    [gridEdgeSize],
  );

  // Memoize camera click handler
  const handleCameraClick = useCallback(
    (camera: any) => {
      selectCamera(camera, 'Video Player');
    },
    [selectCamera],
  );

  // Memoize preview close handler
  const handlePreviewClose = useCallback(() => {
    setPreviewIsVisible(false);
  }, [setPreviewIsVisible]);

  // Memoize volume change handler
  const handleVolumeChange = useCallback(
    (volume: number) => {
      player?.setVolume(volume);
    },
    [player],
  );

  // Memoize mute toggle handler
  const handleMuteToggle = useCallback(
    (isMuted: boolean) => {
      player?.setMute(isMuted);
    },
    [player],
  );

  // Memoize preview banner sx
  const previewBannerSx = useMemo(
    () => ({
      position: 'absolute' as const,
      left: '50%',
      top: isFullscreen ? 80 : 20,
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(0,0,0,0.5)',
      color: 'white',
      padding: '5px 10px',
      borderRadius: '4px',
      fontSize: 14,
      zIndex: 10,
      opacity: 0.7,
      textTransform: 'uppercase' as const,
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.7)',
        opacity: 1,
        cursor: 'pointer',
      },
    }),
    [isFullscreen],
  );

  // Memoize close icon sx
  const closeIconSx = useMemo(
    () => ({
      position: 'absolute' as const,
      right: '-8px',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        opacity: 0.8,
      },
    }),
    [],
  );

  // Memoize scoreboard sx
  const scoreboardSx = useMemo(
    () => ({
      top: 20,
      left: '50%',
      position: 'absolute' as const,
      transform: 'translateX(-50%)',
      backgroundColor: 'black',
    }),
    [],
  );

  // Memoize video controls container sx
  const videoControlsSx = {
    bottom: 0,
    right: 0,
    width: '100%',
    opacity: 1,
  };

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'game-page-main',
    storage: localStorage,
  });

  return (
    <Group
      orientation="vertical"
      style={{ height: '100%' }}
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
    >
      <Panel id="game-player">
        <Box
          ref={playerContainerRef}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            overflow: 'hidden',
            p: 0,
            pl: 1,
          }}
        >
          <Box sx={playerBoxSx} ref={fullscreenRef}>
            <WheelZoom>
              <Box id={VIDEO_PLAYER_ID} />
              {/* OvenPlayer's own error box (warning icon + text): the slate
                  covers reconnects, and a dead stream is reported by the UI. */}
              <GlobalStyles
                styles={{ '.ovenplayer .op-message-container': { display: 'none' } }}
              />
              <FOVOverlay />
              <TargetVideoOverlay />
              <GameSplash
                open={!playbackAvailable || stalled}
                game={game}
                message={stalled ? 'Stream unavailable — reconnecting…' : 'Loading game video'}
              />

              <div style={gradientBackground}></div>

              {playbackAvailable && (
                <>
                  {viewType === ViewType.MOSAIC && (
                    <Box sx={mosaicGridSx}>
                      {Array.isArray(availableCameras) &&
                        availableCameras.length > 0 &&
                        availableCameras.map((c) => (
                          <Box
                            key={c.id}
                            onClick={() => handleCameraClick(c)}
                            sx={CAMERA_QUADRANT_STYLE}
                          >
                            <Box m={1} sx={CAMERA_ANGLE_STYLE}>
                              <RotatingIcon size={24} angle={c.pan ? c.pan - 45 : 0} />
                            </Box>
                            <Box m={1} sx={CAMERA_LABEL_STYLE}>
                              <Typography fontSize={10}>{c.displayName} </Typography>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  )}
                </>
              )}
            </WheelZoom>

            {playbackAvailable && (
              <>
                {!webrtcOverlayVisible && previewIsVisible && (
                  <Tooltip title="You are previewing an HD virtual camera from the full 10.5k video capture and may experience quality loss in the web player. Exported clips maintain full HD quality within your selected framing.">
                    <Box sx={previewBannerSx}>
                      Content Preview
                      <Box onClick={handlePreviewClose} sx={closeIconSx}>
                        <CloseIcon size={14} />
                      </Box>
                    </Box>
                  </Tooltip>
                )}
                <JumpFeedbackOverlay seconds={jumpFeedback} />
                {webrtcOverlayVisible && statsReport && (
                  <WebrtcMetrics
                    toggleStatsVisibility={webrtcOverlayVisible}
                    statsReport={statsReport}
                    isFullScreen={isFullscreen}
                  />
                )}
                <DraggableGamePosition isFullscreen={isFullscreen} />
                {isFullscreen && (
                  <ScoreboardData data={game} shrinkable={true} sx={scoreboardSx}></ScoreboardData>
                )}
                <Box sx={{ position: 'absolute', ...videoControlsSx }} ref={videoControls}>
                  <PlayerControls
                    requestFullscreen={requestFullscreen}
                    exitFullscreen={exitFullscreen}
                    isFullscreen={isFullscreen}
                    onVolumeChange={handleVolumeChange}
                    onMuteToggle={handleMuteToggle}
                    onJumpFeedback={setJumpFeedback}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Panel>
      {!isFullscreen && (
        <>
          <GripSeparator orientation="horizontal" />

          <Panel
            id="game-autoiso"
            minSize={autoIsoPanelMinSize}
            maxSize={400}
            style={{
              paddingLeft: 8,
              paddingBottom: 8,
              paddingRight: 0,
            }}
            defaultSize={autoIsoPanelMinSize}
          >
            <AutoIsoPanelContent />
          </Panel>
        </>
      )}
    </Group>
  );
};

export default GamePageContents;
